import React, {Fragment, ReactNode, useState} from 'react';

import {SerializedError} from '@reduxjs/toolkit';
import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {colors} from 'js-libs/sweetcommons/styles';
import {withRouter, WithRouterProps} from 'react-router';
import styled from 'styled-components';

import {useCreateAcm, useDeleteAcm} from 'app/aws/api';
import {AutoHeightTitleInput} from 'app/components/general/AutoHeightTitleInput';
import {FormControlGroup} from 'app/general/forms';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {useKnowledgeBase, useUpdateKnowledgeBase} from 'app/kbs/api';
import {StyledInfoNotice} from 'app/kbs/components/DomainConfiguration';
import {DataSharingEnabledGate} from 'app/openai/components/DataSharingEnabledGate';
import {ACM_API_URL} from 'app/urls';
import {useUserDetails} from 'app/user/api';
import {SelectUsersSingle} from 'app/user/components/select';
import {convertToBasicUser} from 'app/user/utils';
import {gt} from 'app/utils/i18n';

import {EditKBBlock} from './EditKBBlock';
import {EditKBTheme} from './EditKBTheme';
import {IndentedFormGroup} from './IndentedFormGroup';

import type {UpdateKnowledgeBaseProps} from 'app/kbs/types';
import type {
  BlockFieldNameType,
  KBBlockSerializedType,
  KBThemeSerializedType,
  KnowledgebaseSerializedType,
} from 'sweet-types';

type FlagFieldProps = {
  checked: boolean;
  id: string;
  text: ReactNode;
  disabled?: boolean;
  onChange?: (flag: boolean) => unknown;
};

const FlagField = ({checked, id, text, onChange, disabled}: FlagFieldProps) => (
  <div className="form-check mb-2">
    <input
      className="form-check-input"
      type="checkbox"
      checked={checked}
      onChange={(e) => {
        if (onChange != null) {
          return onChange(e.target.checked);
        }
      }}
      disabled={disabled}
      id={id}
    />
    <label className="form-check-label" htmlFor={id}>
      {text}
    </label>
  </div>
);

interface Props extends WithRouterProps<{hashid: string}> {
  setEditing: (editState: boolean) => unknown;
}

export const EditKBMetadata = withRouter(({params, setEditing}: Props) => {
  const {data: kb} = useKnowledgeBase({kb_id: params.hashid});

  const [kbDraft, setKbDraft] = useState<
    KnowledgebaseSerializedType | undefined
  >(kb && {...kb});
  const [editingTheme, setEditingTheme] =
    useState<KBThemeSerializedType | null>(null);

  const [changes, setChanges] = useState<
    Omit<UpdateKnowledgeBaseProps, 'kb_id'> | Record<string, unknown>
  >({});
  const [domain, setDomain] = useState<string | null | undefined>(kb?.domain);

  const [createAcm, {error: domainError, isLoading: isCreatingAcm}] =
    useCreateAcm();
  const [updateKBData, {error, isLoading: isSaving}] = useUpdateKnowledgeBase();
  const [deleteAcm, {isLoading: isDeletingAcm}] = useDeleteAcm();

  const errors = extractFormErrors(error);
  const domainErrors = extractFormErrors(domainError);
  const contactUserId = kbDraft?.contact_user?.id;

  const isKBPrivate = kbDraft?.private || kb?.private;
  const isKBDisabled = !kb?.enabled || !kbDraft?.enabled;

  const user =
    useUserDetails(
      {id: contactUserId},
      {refetchOnMountOrArgChange: false, skip: contactUserId == null}
    ).data ?? null;

  const handleKbSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (Object.keys(changes).length === 0) {
      return setEditing(false);
    }

    kbDraft &&
      updateKBData({kb_id: kbDraft.hashid, ...changes})
        .unwrap()
        .then(() => setEditing(false));
  };

  const handleCustomDomainSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    if (!domain) {
      return setEditing(false);
    }

    domain &&
      kbDraft &&
      createAcm({
        domain,
        knowledgebase: kbDraft.url,
      })
        .unwrap()
        .then(() => setEditing(false));
  };

  const handleDeleteCustomDomain = () => {
    kb?.acm_hashid &&
      deleteAcm({url: `${ACM_API_URL}${kb.acm_hashid}/`})
        .unwrap()
        .then(() => setDomain(''));
  };

  const updateKB = (updates: Record<string, unknown>) => {
    kbDraft && setKbDraft({...kbDraft, ...updates});
    setChanges({...changes, ...updates});
    return Promise.resolve(true);
  };

  const updateTheme = (theme: KBThemeSerializedType | null): Promise<any> => {
    // time to put the theme onto the kb.
    setEditingTheme(null);
    if (kbDraft) {
      return updateKBData({
        kb_id: kbDraft.hashid,
        theme: theme?.url ?? null,
      }).unwrap();
    }
    return Promise.resolve(false);
  };

  return (
    <>
      <form onSubmit={handleKbSubmit}>
        <DocHeaderStructure
          advancedContent={
            <MetaContentTypeName
              contentType="knowledgebase"
              statusContent={
                kbDraft && (
                  <DocumentMetadataStatusButtons
                    canEdit={true}
                    context={'edit'}
                    contentType="knowledgebase"
                    document={kbDraft}
                  />
                )
              }
            />
          }
          titleContent={
            <h1>
              <FormControlGroup field="name">
                <AutoHeightTitleInput
                  maxLength={128}
                  readOnly={isSaving}
                  className="mt-2"
                  value={kbDraft?.name || ''}
                  autoFocus={true}
                  onChange={(name) => updateKB({name})}
                  placeholder={gt('Knowledge Base Title')}
                />
              </FormControlGroup>
            </h1>
          }
          extraContent={
            <div>
              {errors?.non_field_errors?.map((error) => (
                <p key={error} className="alert alert-danger">
                  {error}
                </p>
              ))}
              <IndentedFormGroup title={gt('Theme')}>
                {kb && (
                  <EditKBTheme
                    kb={kb}
                    editingTheme={editingTheme}
                    updateTheme={updateTheme}
                    setEditingTheme={setEditingTheme}
                  />
                )}
              </IndentedFormGroup>
              <IndentedFormGroup title={gt('Control Access')}>
                <FlagField
                  checked={!!kbDraft?.enabled}
                  id="kb-enabled"
                  text={gt('Enable Knowledge Base')}
                  onChange={(enabled) => updateKB({enabled})}
                />
                <DataSharingEnabledGate
                  sharingEnabledElement={
                    <FlagField
                      checked={!!kbDraft?.sweetai_chat_enabled}
                      id="kb-sweetai_chat_enabled"
                      text={gt('SweetAI Chat Enabled')}
                      onChange={(sweetai_chat_enabled) =>
                        updateKB({sweetai_chat_enabled})
                      }
                    />
                  }
                  sharingDisabledElement={
                    <FlagField
                      checked={false}
                      id="kb-sweetai_chat_enabled"
                      text={gt('SweetAI Chat Enabled')}
                      disabled={true}
                    />
                  }
                />

                <select
                  value={kbDraft?.private ? 'private' : 'public'}
                  className="form-control"
                  id="kb-private-field"
                  onChange={(e) =>
                    updateKB({
                      private: e.target.value === 'private',
                      feedback_external: false,
                    })
                  }
                >
                  <option value="private">
                    {gt(
                      'Private Knowledge Base, only logged in team members may view (if they have the necessary permissions)'
                    )}
                  </option>
                  <option value="public">
                    {gt(
                      'Public Knowledge Base. Any member of the public can view without logging in'
                    )}
                  </option>
                </select>

                <FormControlGroup field="name" className="mt-4">
                  <label htmlFor="redirectionUrl">
                    {gt(
                      'Link to where customers will be redirected after clicking your knowledge base logo.'
                    )}
                  </label>
                  <input
                    type="text"
                    value={kbDraft?.redirection_url ?? ''}
                    placeholder="https://www.sweetprocess.com/procedures/"
                    className={`form-control ${
                      errors?.redirection_url ? 'is-invalid' : ''
                    }`}
                    id="redirectionUrl"
                    onChange={(e) =>
                      updateKB({
                        redirection_url: e.target.value,
                      })
                    }
                  />
                  <>
                    {errors?.redirection_url
                      ? errors?.redirection_url.map((error) => (
                          <div key={error} className="invalid-feedback">
                            {error}
                          </div>
                        ))
                      : null}
                  </>
                </FormControlGroup>
              </IndentedFormGroup>

              <IndentedFormGroup title={gt('Feedback')}>
                <FlagField
                  checked={!!kbDraft?.feedback}
                  id="kb-feedback"
                  text={gt(
                    'Ask your team members for feedback at the end of each article'
                  )}
                  onChange={(feedback) => updateKB({feedback})}
                />

                {!kbDraft?.private ? (
                  <Fragment>
                    <FlagField
                      checked={!!kbDraft?.feedback_external}
                      id="kb-feedback_external"
                      text={gt(
                        'Ask members of the public for feedback at the end of each article'
                      )}
                      disabled={kbDraft?.private}
                      onChange={(feedback_external) =>
                        updateKB({
                          feedback_external,
                        })
                      }
                    />
                    <FlagField
                      checked={!!kbDraft?.questions}
                      id="kb-questions"
                      text={gt('Show a contact form')}
                      onChange={(questions) => updateKB({questions})}
                    />
                    {kbDraft?.questions ? (
                      <div className="border-left">
                        <FormControlGroup className="col" field="contact_user">
                          <label className="col-form-label-sm mb-0">
                            {gt('Send questions to')}
                          </label>
                          <SelectUsersSingle
                            placeholder={gt(
                              'Select a teammate to receive contact messages'
                            )}
                            selected={contactUserId != null ? user : null}
                            onChange={(user) =>
                              updateKB({
                                contact_user_url:
                                  user != null ? user.url : null,
                                contact_user:
                                  user != null
                                    ? convertToBasicUser(user)
                                    : null,
                              })
                            }
                          />
                          <span className="small text-secondary font-italic ">
                            {gt(
                              'If not set, questions will be sent to account owner.'
                            )}
                          </span>
                        </FormControlGroup>
                      </div>
                    ) : null}
                  </Fragment>
                ) : null}
              </IndentedFormGroup>

              <div className="form-group text-right mb-0">
                <button
                  disabled={isSaving}
                  type="submit"
                  className="btn btn-primary"
                >
                  {isSaving
                    ? gt('Saving...')
                    : Object.keys(changes).length === 0
                    ? gt('Done')
                    : gt('Save changes')}
                </button>
              </div>

              <IndentedFormGroup title="Text Blocks">
                <p>
                  {gt(
                    "Customize your knowledge base by editing text blocks to match your brand's voice and information needs."
                  )}
                </p>
                {kb?.url != null ? (
                  <>
                    <EditKBBlock
                      block={blockOrDefault(
                        'search_placeholder',
                        kb,
                        'Type your question here...'
                      )}
                      knowledgebase={kb.url}
                      label={gt('Search Placeholder')}
                    />
                    <EditKBBlock
                      block={blockOrDefault('breadcrumb_home_text', kb, 'Help')}
                      knowledgebase={kb.url}
                      label={gt('Breadcrumb Home Text')}
                    />
                    <EditKBBlock
                      block={blockOrDefault(
                        'index_category_heading',
                        kb,
                        'Common Help Topics'
                      )}
                      knowledgebase={kb.url}
                      label={gt('Index Page Category Heading')}
                    />
                    <EditKBBlock
                      block={blockOrDefault(
                        'category_heading',
                        kb,
                        'Main Topics'
                      )}
                      knowledgebase={kb.url}
                      label={gt('Sidebar Category Heading')}
                    />
                    <EditKBBlock
                      block={blockOrDefault(
                        'contact_form_heading',
                        kb,
                        'Looking for something else?'
                      )}
                      knowledgebase={kb.url}
                      label={gt('Contact Form Heading')}
                    />
                    <EditKBBlock
                      block={blockOrDefault(
                        'contact_form_subject_label',
                        kb,
                        'Subject'
                      )}
                      knowledgebase={kb.url}
                      label={gt('Contact Form Subject Label')}
                    />
                    <EditKBBlock
                      block={blockOrDefault(
                        'contact_form_body_label',
                        kb,
                        'What is your question or feedback? Ask us anything :)'
                      )}
                      knowledgebase={kb.url}
                      label={gt('Contact Form Message Label')}
                    />
                    <EditKBBlock
                      block={blockOrDefault(
                        'contact_page_heading',
                        kb,
                        "We're here to help"
                      )}
                      knowledgebase={kb.url}
                      label={gt('Contact Page Heading')}
                    />
                  </>
                ) : null}
              </IndentedFormGroup>
            </div>
          }
        />
      </form>
      <form onSubmit={handleCustomDomainSubmit}>
        <StyledJumbotronHeader>
          <div className="container">
            <IndentedFormGroup title={gt('Custom branded domain')}>
              <label htmlFor="customDomain">
                {gt('Customize your knowledge base URL to match your brand.')}
                <a
                  href="/kb/8LBTequD/article/45vtYf5J5a/giving-your-knowledge-base-a-custom-branded-subdomain/"
                  target="_blank"
                  className="ml-2"
                >
                  {gt('Learn more')}
                </a>
              </label>
              <p></p>
              <input
                type="text"
                value={domain ?? ''}
                placeholder="help.yourcompany.com"
                className={`form-control ${
                  errors?.domain_url ? 'is-invalid' : ''
                }`}
                disabled={kb?.acm_hashid != null || isKBDisabled || isKBPrivate}
                id="customDomain"
                onChange={(e) => setDomain(e.target.value)}
              />
              <>
                {domainErrors?.domain
                  ? domainErrors?.domain.map((error) => (
                      <div key={error} className="invalid-feedback">
                        {error}
                      </div>
                    ))
                  : null}
                {domainErrors?.knowledgebase
                  ? domainErrors?.knowledgebase.map((error) => (
                      <div key={error} className="invalid-feedback">
                        {error}
                      </div>
                    ))
                  : null}
                {kb?.acm_hashid && (
                  <button
                    className="mt-2 btn btn-sm text-danger btn-link"
                    type="button"
                    onClick={handleDeleteCustomDomain}
                  >
                    {gt(isDeletingAcm ? 'Deleting...' : 'Remove this domain')}
                  </button>
                )}
                {kb?.acm_hashid && isKBPrivate ? (
                  <NoticeForPrivateRemoval />
                ) : kb?.acm_hashid && isKBDisabled ? (
                  <NoticeForDisabledRemoval />
                ) : null}

                {!kb?.acm_hashid && isKBPrivate ? (
                  <NoticeWhyCustomPrivateKBOptionUnavailable />
                ) : !kb?.acm_hashid && isKBDisabled ? (
                  <NoticeWhyCustomDisabledKBOptionUnavailable />
                ) : null}
              </>
            </IndentedFormGroup>
            {!kb?.acm_hashid && (
              <div className="form-group text-right mb-0">
                <button
                  disabled={
                    isCreatingAcm || isKBDisabled || isKBPrivate || !domain
                  }
                  type="submit"
                  className="btn btn-primary"
                >
                  {isCreatingAcm
                    ? gt('Setting up domain...')
                    : gt('Setup Domain')}
                </button>
              </div>
            )}
            {isCreatingAcm && (
              <StyledInfoNotice className="d-flex p-4 my-4 flex-wrap align-items-center">
                <p className="m-0">
                  {gt('Your domain setup is in progress...')}
                </p>
              </StyledInfoNotice>
            )}
          </div>
        </StyledJumbotronHeader>
      </form>
    </>
  );
});

function extractFormErrors(
  error: SerializedError | FetchBaseQueryError | undefined
): {[field: string]: Array<string>} {
  let errors: {[field: string]: Array<string>} = {};

  if (error != null && 'status' in error && error.status === 400) {
    const errorData = error.data as Record<string, Array<string>>;
    for (const field of Object.keys(errorData)) {
      errors[field] = errorData[field];
    }
  }
  return errors;
}

const StyledJumbotronHeader = styled.header.attrs({
  id: 'document-head',
  className: 'jumbotron px-0 mb-0 pt-0',
})`
  overflow-wrap: break-word;
  background-color: ${colors.lightBg};
`;

const NoticeForPrivateRemoval = () => (
  <p className="alert alert-danger mt-2">
    {gt(
      'This knowledge base is private - The custom domain is queued for removal.'
    )}
  </p>
);

const NoticeForDisabledRemoval = () => (
  <p className="alert alert-danger mt-2">
    {gt(
      'This knowledge base is not enabled - The custom domain is queued for removal.'
    )}
  </p>
);

const NoticeWhyCustomPrivateKBOptionUnavailable = () => (
  <div className="alert alert-warning mt-2" role="alert">
    <h4 className="alert-heading">{gt('Important Notice')}</h4>
    <p>
      {gt(
        "Currently, you can't add a custom domain to your private knowledge base because your account sign-in won't work on a different domain."
      )}{' '}
      {gt(
        "We're keen to understand your needs better. If this feature is a feature you're interested in, please let us know!"
      )}{' '}
      {gt('Your feedback helps us prioritize new features.')}
    </p>
    <hr />
    <p className="mb-0">
      {gt(
        'For any questions or to express your interest in custom domain functionality, please reach out to our support team.'
      )}
    </p>
  </div>
);

const NoticeWhyCustomDisabledKBOptionUnavailable = () => (
  <div className="alert alert-warning mt-2" role="alert">
    <h4 className="alert-heading">{gt('Important Notice')}</h4>
    <p>
      {gt(
        'This knowledge base is not currently enabled. To add a custom domain please first re-enable your knowledge base.'
      )}
    </p>
  </div>
);

function createDefaultKBBlock(
  name: BlockFieldNameType,
  knowledgebase: string,
  defaultContent: string
): KBBlockSerializedType {
  return {
    url: '',
    name,
    content: defaultContent,
    content_type: 'kbblock',
    knowledgebase,
  };
}

function blockOrDefault(
  name: BlockFieldNameType,
  kb: KnowledgebaseSerializedType,
  defaultContent: string
): KBBlockSerializedType {
  return kb.blocks[name] ?? createDefaultKBBlock(name, kb.url, defaultContent);
}
